import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ExterniKontaktyService from "./ExterniKontaktyService";
import { useToast } from "../../../components/toast/useToast";
import type {
    ExterniKontakt,
    ExterniKontaktRequest,
    RodinnyPinStatus,
} from "../generated/models/ExterniKontakt";

export const useExterniKontaktyQuery = () => {
    return useQuery<ExterniKontakt[], Error>({
        queryKey: ['externi-kontakty'],
        queryFn: () => ExterniKontaktyService.getAll(),
        staleTime: 30 * 1000,
    });
};

export const useCreateExterniKontaktMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<ExterniKontakt, Error, ExterniKontaktRequest>({
        mutationFn: (data) => ExterniKontaktyService.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['externi-kontakty'] });
            showToast("Kontakt byl vytvořen.", "success");
        },
        onError: (error) => showToast(error.message || "Nepodařilo se vytvořit kontakt.", "error"),
    });
};

export const useUpdateExterniKontaktMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<ExterniKontakt, Error, { id: number; data: ExterniKontaktRequest }>({
        mutationFn: ({ id, data }) => ExterniKontaktyService.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['externi-kontakty'] });
            showToast("Kontakt byl upraven.", "success");
        },
        onError: (error) => showToast(error.message || "Nepodařilo se upravit kontakt.", "error"),
    });
};

export const useDeleteExterniKontaktMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<unknown, Error, number>({
        mutationFn: (id) => ExterniKontaktyService.remove(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['externi-kontakty'] });
            showToast("Kontakt byl smazán.", "success");
        },
        onError: (error) => showToast(error.message || "Nepodařilo se smazat kontakt.", "error"),
    });
};

// ---- Rodinny PIN: userId = admin sprava cizi(ho) PINu; bez userId = self-service ----

export const useRodinnyPinStatusQuery = (userId?: number, opts?: { enabled?: boolean }) => {
    return useQuery<RodinnyPinStatus, Error>({
        queryKey: ['rodinny-pin', userId ?? 'me'],
        queryFn: () => userId
            ? ExterniKontaktyService.getAdminPinStatus(userId)
            : ExterniKontaktyService.getMyPinStatus(),
        enabled: opts?.enabled ?? true,
        staleTime: 0,
    });
};

export const useGenerateRodinnyPinMutation = (userId?: number) => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<{ pin: string }, Error, void>({
        mutationFn: () => userId
            ? ExterniKontaktyService.generateAdminPin(userId)
            : ExterniKontaktyService.generateMyPin(),
        onSuccess: () => {
            // BEZ toastu - PIN se zobrazuje primo v dialogu (jednorazove)
            queryClient.invalidateQueries({ queryKey: ['rodinny-pin', userId ?? 'me'] });
        },
        onError: (error) => showToast(error.message || "Nepodařilo se vygenerovat PIN.", "error"),
    });
};

export const useInvalidateRodinnyPinMutation = (userId?: number) => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<unknown, Error, void>({
        mutationFn: () => userId
            ? ExterniKontaktyService.invalidateAdminPin(userId)
            : ExterniKontaktyService.invalidateMyPin(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['rodinny-pin', userId ?? 'me'] });
            showToast("Rodinný PIN byl zneplatněn.", "success");
        },
        onError: (error) => showToast(error.message || "Nepodařilo se zneplatnit PIN.", "error"),
    });
};
